import { forwardRef, memo, type MouseEventHandler } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
// Material UI imports
import Avatar from '@mui/material/Avatar';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
// Material Icon imports
import CloseIcon from '@mui/icons-material/Close';
import CloseThick from 'mdi-material-ui/CloseThick';
// SCSS imports
import { button, smallButton, avatar, smallAvatar, transparentAvatar, active, inactive } from './CloseIconButton.module.scss';

type CloseIconButtonProps = {
  readonly small?: boolean;
  readonly transparent?: boolean;
  readonly onClick: MouseEventHandler<HTMLButtonElement>;
  readonly disabled?: boolean;
  readonly sx?: IconButtonProps['sx'];
};

const CloseIconButton = forwardRef<HTMLButtonElement, CloseIconButtonProps>(({
  small = false,
  transparent = false,
  onClick,
  disabled = false,
  sx
}, ref) => {
  const { formatMessage } = useIntl();
  return (
    <IconButton
        ref={ref}
        sx={sx}
        size="medium"
        aria-label={formatMessage({ id: 'common.button.close' })}
        className={small ? smallButton : button}
        onClick={onClick}
        disabled={disabled}
    >
      <Avatar
          variant="circular"
          className={clsx({
            [smallAvatar]: small,
            [transparentAvatar]: transparent,
            [avatar]: !small && !transparent,
            [active]: !disabled,
            [inactive]: disabled
          })}
      >
        {transparent
          ? <CloseIcon fontSize="inherit"/>
          : <CloseThick fontSize="inherit"/>}
      </Avatar>
    </IconButton>
  );
});

CloseIconButton.displayName = 'CloseIconButton';

export default memo(CloseIconButton);
